<template>
  <div class="app-sidebar">
    <div class="logo">
      <img
        src="../../assets/logo.png"
        alt="Logo"
        class="logo-img"
      >
      <span
        v-if="!collapsed"
        class="title"
      >{{ systemTitle }}</span>
    </div>
    <el-scrollbar>
      <el-menu
        :default-active="activeMenu"
        class="sidebar-menu"
        :collapse="collapsed"
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
        unique-opened
        router
      >
        <template
          v-for="(item, index) in menuItems"
          :key="index"
        >
          <!-- 含子菜单的情况 -->
          <el-sub-menu
            v-if="item.children && item.children.length"
            :index="item.path"
          >
            <template #title>
              <component :is="item.icon" v-if="item.icon" class="menu-icon" />
              <span>{{ item.title }}</span>
            </template>

            <!-- 遍历二级菜单 -->
            <template v-for="(child, i) in item.children" :key="i">
              <!-- 二级菜单有子菜单（三级菜单） -->
              <el-sub-menu
                v-if="child.children && child.children.length"
                :index="child.path"
              >
                <template #title>
                  <component :is="child.icon" v-if="child.icon" class="menu-icon" />
                  <span>{{ child.title }}</span>
                </template>
                <el-menu-item
                  v-for="(subChild, j) in child.children"
                  :key="j"
                  :index="subChild.path"
                >
                  <component :is="subChild.icon" v-if="subChild.icon" class="menu-icon" />
                  <span>{{ subChild.title }}</span>
                </el-menu-item>
              </el-sub-menu>

              <!-- 二级菜单没有子菜单 -->
              <el-menu-item
                v-else
                :index="child.path"
              >
                <component :is="child.icon" v-if="child.icon" class="menu-icon" />
                <span>{{ child.title }}</span>
              </el-menu-item>
            </template>
          </el-sub-menu>

          <!-- 没有子菜单的情况 -->
          <el-menu-item
            v-else
            :index="item.path"
          >
            <component :is="item.icon" v-if="item.icon" class="menu-icon" />
            <span>{{ item.title }}</span>
          </el-menu-item>
        </template>
      </el-menu>
    </el-scrollbar>
  </div>
</template>

<script setup>
import { ref, computed, provide, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  HomeOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  ApiOutlined,
  SettingOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ToolOutlined,
  CalculatorOutlined,
  HistoryOutlined,
  ControlOutlined,
  FileSearchOutlined,
  ExperimentOutlined,
  AppstoreAddOutlined,
  UnorderedListOutlined,
  ClusterOutlined,
  ShoppingOutlined,
  ApartmentOutlined,
  BankOutlined,
  GlobalOutlined,
  DollarCircleOutlined,
  AlertOutlined,
  SolutionOutlined,
  QuestionCircleOutlined,
  BarChartOutlined,
  GiftOutlined,
  FileSyncOutlined,
  PieChartOutlined,
  FundProjectionScreenOutlined,
  AreaChartOutlined,
  DotChartOutlined,
  PartitionOutlined,
  BellOutlined,
  TrophyOutlined,
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  DashboardOutlined,
  BugOutlined,
  ReadOutlined,
  ShopOutlined,
  ForkOutlined,
  NodeIndexOutlined,
  DeploymentUnitOutlined,
  ConnectionOutlined,
  BranchesOutlined,
  LineChartOutlined,
  RadarChartOutlined,
  FundViewOutlined,
  FundOutlined,
  WarningOutlined,
  TagsOutlined,
  RiseOutlined,
  LockOutlined,
  AuditOutlined,
  FileProtectOutlined
} from '@ant-design/icons-vue'

// 路由和系统模式
const route = useRoute()
const router = useRouter()

// 侧边栏折叠状态
const collapsed = ref(false)

// 注入折叠状态（如果父组件提供了）
import { inject } from 'vue'
const injectedUpdateCollapsed = inject('updateSidebarCollapsed', null)

// 原型系统菜单 - 优化后的菜单名称
const originalMenuItems = [
  {
    title: '系统首页',
    path: '/',
    icon: HomeOutlined
  },
  {
    title: '经营大脑',
    path: '/operation-brain',
    icon: DeploymentUnitOutlined
  },
  {
    title: '流向数据',
    path: '/flow-data',
    icon: BarChartOutlined,
    children: [
      { title: '数据总览', path: '/flow-data', icon: PieChartOutlined },
      { title: '流向数据详情', path: '/flow-data/detail', icon: FileSearchOutlined },
      { title: '纯销数据详情', path: '/flow-data/pure-sales-detail', icon: AreaChartOutlined },
      { title: '库存数据详情', path: '/flow-data/inventory-detail', icon: DatabaseOutlined },
      { title: '退货数据详情', path: '/flow-data/return-detail', icon: FileTextOutlined },
      { title: '配送数据详情', path: '/flow-data/delivery-detail', icon: ThunderboltOutlined },
      { title: '纯销数据', path: '/flow-data/pure-sales', icon: AreaChartOutlined },
      { title: '配送数据', path: '/flow-data/delivery', icon: ThunderboltOutlined },
      { title: '库存数据', path: '/flow-data/inventory', icon: DatabaseOutlined },
      { title: '原始记录', path: '/raw-data/list', icon: FileSearchOutlined },
      { title: '映射配置', path: '/raw-data/mapping/template-config', icon: ApiOutlined }
    ]
  },
  {
    title: '数据处理',
    path: '/data-process',
    icon: FolderOpenOutlined,
    children: [
      { title: '精简导入', path: '/data-process/import/simplified', icon: ThunderboltOutlined },
      { title: '数据导入', path: '/data-process/import/file', icon: FileTextOutlined },
      { title: '数据导入（新）', path: '/data-process/import/file-new', icon: FileTextOutlined },
      { title: 'DDI管理', path: '/data-process/ddi-management', icon: ApiOutlined },
      { title: '网站配置', path: '/data-process/website-config', icon: SettingOutlined },
      { title: '自定义验证器', path: '/data-process/accounting/custom-validators', icon: ExperimentOutlined },
      { title: '映射模板管理', path: '/data-process/field-mapping-template', icon: ApiOutlined },
      { title: '处理记录', path: '/data-process/history', icon: HistoryOutlined }
    ]
  },
  {
    title: '规则管理',
    path: '/rule-management',
    icon: ControlOutlined,
    children: [
      { title: '规则列表', path: '/rule-management/list', icon: FileSearchOutlined },
      { title: '规则测试', path: '/rule-management/tester', icon: ExperimentOutlined }
    ]
  },
  {
    title: '主数据',
    path: '/master-data',
    icon: ClusterOutlined,
    children: [
      // 客户相关
      { title: '客户管理', path: '/master-data/customer/unified', icon: NodeIndexOutlined },
      { title: '待入客户', path: '/master-data/customer/pending', icon: ForkOutlined },
      { title: '类型配置', path: '/master-data/customer/type-management', icon: DatabaseOutlined },

      // 人员管理
      { title: '代表管理', path: '/master-data/representative', icon: TeamOutlined },

      // 基础数据
      { title: '产品数据', path: '/master-data/product', icon: ShoppingOutlined },
      { title: '规格数据', path: '/master-data/spec', icon: TagsOutlined },
      { title: '组织架构', path: '/master-data/organization', icon: ApartmentOutlined },
      { title: '区域管理', path: '/master-data/region/management', icon: GlobalOutlined },
      { title: '价格管理', path: '/master-data/price', icon: DollarCircleOutlined }
    ]
  },
  {
    title: '异常管理',
    path: '/anomaly',
    icon: AlertOutlined,
    children: [
      { title: '异常检测', path: '/anomaly-center', icon: BugOutlined },
      { title: '检测配置', path: '/anomaly-center/config', icon: SettingOutlined },
      { title: '申诉中心', path: '/flow-appeal', icon: SolutionOutlined }
    ]
  },
  {
    title: '返利管理',
    path: '/rebate-calculation',
    icon: GiftOutlined,
    children: [
      { title: '政策管理', path: '/rebate-calculation/policy-management', icon: FileSyncOutlined },
      { title: '返利核算', path: '/rebate-calculation/calculator', icon: CalculatorOutlined },
      { title: '客户结算', path: '/rebate-calculation/settlement', icon: DollarCircleOutlined },
      { title: '返利台账', path: '/rebate-calculation/ledger', icon: BookOutlined },
      { title: '返利分析', path: '/rebate-calculation/analysis', icon: LineChartOutlined },
      { title: '返利报表', path: '/rebate-calculation/reports', icon: PieChartOutlined }
    ]
  },
  {
    title: '数据分析中心',
    path: '/data-analysis-new',
    icon: LineChartOutlined,
    children: [
      // 流向数据分析（二级菜单）
      {
        title: '流向数据分析',
        path: '/data-analysis-new/flow',
        icon: FundProjectionScreenOutlined,
        children: [
          { title: '流向全景分析', path: '/data-analysis-new/flow/overview', icon: DashboardOutlined },
          { title: '流向明细查询', path: '/data-analysis-new/flow/detail', icon: UnorderedListOutlined },
          { title: '异常流向分析', path: '/data-analysis-new/flow/abnormal', icon: AlertOutlined }
        ]
      },

      // 返利ROI分析（二级菜单）
      {
        title: '返利ROI分析',
        path: '/data-analysis-new/rebate-roi',
        icon: FundOutlined,
        children: [
          { title: 'ROI综合仪表板', path: '/data-analysis-new/rebate-roi/dashboard', icon: DashboardOutlined },
          { title: '返利ROI概览', path: '/data-analysis-new/rebate-roi/overview', icon: BarChartOutlined },
          { title: '多维度ROI分析', path: '/data-analysis-new/rebate-roi/multi-dimension', icon: BarChartOutlined },
          { title: '政策效果对比', path: '/data-analysis-new/rebate-roi/policy-comparison', icon: RadarChartOutlined },
          { title: '政策效果评估', path: '/data-analysis-new/rebate-roi/policy-effect', icon: FundViewOutlined },
          { title: '成本效益分析', path: '/data-analysis-new/rebate-roi/cost-benefit', icon: LineChartOutlined }
        ]
      },

      // 客户洞察分析（二级菜单）
      {
        title: '客户洞察分析',
        path: '/data-analysis-new/customer-insight',
        icon: TeamOutlined,
        children: [
          { title: '客户RFM概览', path: '/data-analysis-new/customer-insight/rfm-overview', icon: DashboardOutlined },
          { title: '客户分层列表', path: '/data-analysis-new/customer-insight/rfm-list', icon: UnorderedListOutlined },
          { title: 'RFM客户分群', path: '/data-analysis-new/customer-insight/rfm-segmentation', icon: ClusterOutlined },
          { title: '客户价值分析', path: '/data-analysis-new/customer-insight/value-analysis', icon: RiseOutlined },
          { title: '客户流失管理', path: '/data-analysis-new/customer-insight/churn-management', icon: WarningOutlined },
          { title: '标签管理', path: '/data-analysis-new/customer-insight/tag-management', icon: TagsOutlined }
        ]
      },

      // 产品市场分析（二级菜单）
      {
        title: '产品市场分析',
        path: '/data-analysis-new/product-market',
        icon: ShoppingOutlined,
        children: [
          { title: '产品销量排名', path: '/data-analysis-new/product-market/sales-ranking', icon: TrophyOutlined },
          { title: '市场占有率', path: '/data-analysis-new/product-market/market-share', icon: PieChartOutlined },
          { title: '竞品对比分析', path: '/data-analysis-new/product-market/competitor-comparison', icon: ForkOutlined }
        ]
      },

      // 销售团队管理（二级菜单）
      {
        title: '销售团队管理',
        path: '/data-analysis-new/sales-team',
        icon: TeamOutlined,
        children: [
          { title: '代表考核分析', path: '/data-analysis-new/sales-team/performance', icon: UserOutlined },
          { title: '团队效能分析', path: '/data-analysis-new/sales-team/team-collaboration', icon: TeamOutlined },
          { title: '拜访管理分析', path: '/data-analysis-new/sales-team/task-management', icon: AreaChartOutlined }
        ]
      },

      // 经营驾驶舱（二级菜单）
      {
        title: '经营驾驶舱',
        path: '/data-analysis-new/business-cockpit',
        icon: DashboardOutlined,
        children: [
          { title: '核心指标监控', path: '/data-analysis-new/business-cockpit/core-metrics', icon: BarChartOutlined },
          { title: '智能预警中心', path: '/data-analysis-new/business-cockpit/business-alerts', icon: BellOutlined },
          { title: '数据大屏', path: '/data-analysis-new/business-cockpit/data-dashboard', icon: FundProjectionScreenOutlined }
        ]
      }
    ]
  },
  {
    title: '数据分析(旧)',
    path: '/data-analysis',
    icon: FundProjectionScreenOutlined,
    children: [
      { title: '进销存分析', path: '/data-analysis/balance-analysis', icon: AreaChartOutlined },
      { title: '渠道分析', path: '/data-analysis/channel-analysis', icon: DotChartOutlined },
      { title: '多维分析', path: '/data-analysis/multi-dimension', icon: PartitionOutlined },
      { title: '终端穿透', path: '/data-analysis/terminal-penetration', icon: ConnectionOutlined },
      { title: '市场洞察', path: '/data-analysis/market-insight', icon: FundProjectionScreenOutlined },
      { title: '库存预警', path: '/data-analysis/inventory-alert', icon: BellOutlined },
      { title: '销量排行', path: '/data-analysis/sales-ranking', icon: TrophyOutlined }
    ]
  },
  {
    title: '审计支撑中心',
    path: '/audit-support',
    icon: AuditOutlined,
    children: [
      { title: '审计支撑工作台', path: '/audit-support/workbench', icon: DashboardOutlined },
      { title: '批次核验列表', path: '/audit-support/verification/batch-list', icon: CheckSquareOutlined },
      { title: '风险对象列表', path: '/audit-support/risk/object-list', icon: AlertOutlined },
      { title: '风险规则配置', path: '/audit-support/risk/rule-config', icon: ControlOutlined },
      { title: '问题台账', path: '/audit-support/issue/list', icon: SolutionOutlined },
      { title: '审计报告中心', path: '/audit-support/report/center', icon: FileProtectOutlined }
    ]
  },
  {
    title: '系统管理',
    path: '/system-settings',
    icon: SettingOutlined,
    children: [
      { title: '用户管理', path: '/system/user-management', icon: UserOutlined },
      { title: '角色管理', path: '/system/role-management', icon: TeamOutlined },
      { title: '权限管理', path: '/system/permission-management', icon: LockOutlined },
      { title: 'IP白名单', path: '/system/ip-whitelist', icon: GlobalOutlined },
      { title: '数据字典', path: '/system-settings/dictionary-management', icon: BookOutlined },
      { title: '系统集成', path: '/system-settings/integration', icon: ApiOutlined },
      { title: 'API测试', path: '/system-settings/api-tester', icon: ThunderboltOutlined },
      { title: '性能监控', path: '/system-settings/performance-monitor', icon: DashboardOutlined },
      { title: '集成测试', path: '/system-settings/integration-tester', icon: BugOutlined },
      { title: 'API文档', path: '/system-settings/api-docs', icon: ReadOutlined }
    ]
  }
]

// 代理商系统菜单 - 优化后的菜单名称
const agentMenuItems = [
  {
    title: '办公中心',
    path: '/agent/daily-office',
    icon: BellOutlined,
    children: [
      { title: '审批管理', path: '/agent/daily-office/approval', icon: CheckCircleOutlined },
      { title: '备忘录', path: '/agent/daily-office/memo', icon: FileTextOutlined },
      { title: '消息中心', path: '/agent/daily-office/notification', icon: BellOutlined }
    ]
  },
  {
    title: '基础数据',
    path: '/agent/basic-data',
    icon: DatabaseOutlined,
    children: [
      { title: '终端管理', path: '/agent/basic-data/terminal', icon: BankOutlined },
      { title: '客户管理', path: '/agent/basic-data/customer', icon: TeamOutlined },
      { title: '医院备案', path: '/agent/basic-data/hospital-filing', icon: SolutionOutlined },
      { title: '产品管理', path: '/agent/basic-data/product', icon: ShoppingOutlined },
      { title: '业务员', path: '/agent/basic-data/salesman', icon: UserOutlined },
      { title: '数据中心', path: '/agent/basic-data/master-data-center', icon: ClusterOutlined }
    ]
  },
  {
    title: '业务管理',
    path: '/agent/business',
    icon: ShopOutlined,
    children: [
      { title: '竞标管理', path: '/agent/business/bidding', icon: TrophyOutlined },
      { title: '销售管理', path: '/agent/business/sales', icon: ShoppingOutlined },
      { title: '考核管理', path: '/agent/business/assessment', icon: CheckSquareOutlined }
    ]
  },
  {
    title: '财务管理',
    path: '/agent/finance',
    icon: DollarCircleOutlined,
    children: [
      { title: '费用设置', path: '/agent/finance/fee-setting', icon: DollarCircleOutlined },
      { title: '货款管理', path: '/agent/finance/payment', icon: CalculatorOutlined },
      { title: '结算管理', path: '/agent/finance/settlement', icon: CalculatorOutlined }
    ]
  },
  {
    title: '异常管理',
    path: '/anomaly',
    icon: AlertOutlined,
    children: [
      { title: '异常检测', path: '/anomaly-center', icon: BugOutlined },
      { title: '检测配置', path: '/anomaly-center/config', icon: SettingOutlined }
    ]
  },
  {
    title: '报表中心',
    path: '/agent/reports',
    icon: BarChartOutlined
  },
  {
    title: '数据分析',
    path: '/agent/analysis',
    icon: PieChartOutlined
  },
  {
    title: '系统设置',
    path: '/agent/system',
    icon: SettingOutlined
  }
]

// 根据当前路由动态切换菜单
const menuItems = computed(() => {
  // 如果是代理商系统路由
  if (route.path.startsWith('/agent')) {
    return agentMenuItems
  }
  // 否则显示原型系统菜单
  return originalMenuItems
})

// 监听路由变化，更新系统标题
const systemTitle = computed(() => {
  if (route.path.startsWith('/agent')) {
    return '药品代理商系统'
  }
  return '药品流向系统'
})

// 当前激活的菜单
const activeMenu = computed(() => {
  const { meta, path } = route
  if (meta.activeMenu) {
    return meta.activeMenu
  }
  return path
})
</script>

<style scoped>
/* 侧边栏整体布局 */
.app-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #304156;
  transition: width 0.3s;
  overflow: hidden;
}

/* Logo 区域 */
.logo {
  height: 60px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  background-color: #263445;
  flex-shrink: 0;
}

.logo-img {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  margin-left: 12px;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.3s;
}

/* 菜单区域滚动条 */
.el-scrollbar {
  flex: 1;
  width: 100%;
}

.sidebar-menu {
  border-right: none;
  width: 100% !important;
}

.sidebar-menu:not(.el-menu--collapse) {
  width: 220px !important;
}

/* 图标样式 */
.menu-icon {
  margin-right: 12px;
  font-size: 16px;
  vertical-align: middle;
}

/* 折叠状态下的补丁 */
.el-menu--collapse .menu-icon {
  margin-right: 0;
}
</style> 